/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Injectable } from '@angular/core';
import {
  AbstractRestService,
  arrayOf,
  identity,
  none,
  privilegedTokenOf,
  RestSettings,
  SlicedDataSource,
} from '@gh/core-data';
import { Observable } from 'rxjs';
import { CommunicationAttachmentDto } from '../communications';
import {
  AccountingWipApplyRequestDto,
  AccountingWipApplyResponseDto,
  AccountingWipCreateAndPrintReportDto,
  AccountingWipCreateAndPrintRequestDto,
  AccountingWipInitDto,
  AccountingWipJournalLogItemDto,
  AccountingWipPrintRequestDto,
  AccountingWipRecalculateRequestDto,
  AccountingWipRecalculateResponseDto,
  AccountingWipReportRequestDto,
} from './accounting-wip.dto';
import {
  AccountingWipApplyRequestMapper,
  AccountingWipApplyResponseMapper, AccountingWipCreateAndPrintRequestMapper,
  AccountingWipInitMapper,
  AccountingWipJournalLogItemMapper,
  AccountingWipPrintRequestMapper,
  AccountingWipRecalculateRequestMapper,
  AccountingWipRecalculateResponseMapper,
  AccountingWipReportRequestMapper,
} from './accounting-wip.mapper';

@Injectable()
export class AccountingWipRestService extends AbstractRestService {
  constructor(private settings: RestSettings) {
    super(settings);
  }

  init(): Observable<AccountingWipInitDto> {
    return this.httpGet(`${this.accounting}/accounting/wip/init`, AccountingWipInitMapper);
  }

  apply(request: AccountingWipApplyRequestDto): Observable<AccountingWipApplyResponseDto> {
    return this.httpPut(
      `${this.accounting}/accounting/wip/apply`,
      request,
      AccountingWipApplyRequestMapper,
      AccountingWipApplyResponseMapper);
  }

  closeEmptyYear(request: AccountingWipRecalculateRequestDto): Observable<AccountingWipRecalculateResponseDto> {
    return this.httpPut(
      `${this.accounting}/accounting/wip/close-empty-financial-year`,
      request,
      privilegedTokenOf(AccountingWipRecalculateRequestMapper),
      AccountingWipRecalculateResponseMapper);
  }

  recalculate(request: AccountingWipRecalculateRequestDto): Observable<AccountingWipRecalculateResponseDto> {
    return this.httpPut(
      `${this.accounting}/accounting/wip/recalculate`,
      request,
      AccountingWipRecalculateRequestMapper,
      AccountingWipRecalculateResponseMapper);
  }

  findLogItemsAsDataSource(): SlicedDataSource<AccountingWipJournalLogItemDto> {
    return this.createSlicedDataSourceGet(`${this.accounting}/accounting/wip/logs`, arrayOf(AccountingWipJournalLogItemMapper));
  }

  print(request: AccountingWipPrintRequestDto): Observable<void> {
    return this.httpPut(`${this.accounting}/accounting/wip/print`, request, AccountingWipPrintRequestMapper, none());
  }

  report(request: AccountingWipReportRequestDto): Observable<CommunicationAttachmentDto> {
    return this.httpGet(`${this.bp}/accounting/wip/report`, request, AccountingWipReportRequestMapper, identity());
  }

  createAndPrintWipJobsReport(request: AccountingWipCreateAndPrintRequestDto): Observable<AccountingWipCreateAndPrintReportDto> {
    return this.httpPut(`${this.bp}/accounting/wip/jobs-report`, request, AccountingWipCreateAndPrintRequestMapper, identity());
  }
}
